import { AgentDelegationKind, AgentDelegationTarget } from '@this+that/models';
import { StateCreator } from 'zustand';

import {
  AgentDelegationTargetsFetcher,
  AgentDelegationTargetsSlice,
  AgentDelegationTargetsState,
  AgentDelegationTargetsStore,
} from './types';

const inFlightRequests = new Map<
  AgentDelegationKind,
  Promise<AgentDelegationTarget[]>
>();

function createEmptySlice(): AgentDelegationTargetsSlice {
  return { targets: [], fetchedAt: null, isLoading: false };
}

function createInitialSlices(): AgentDelegationTargetsState['slices'] {
  return {
    [AgentDelegationKind.AGENT_RUN]: createEmptySlice(),
    [AgentDelegationKind.LOCAL_MCP_CODING]: createEmptySlice(),
  };
}

function isSliceFresh(
  slice: AgentDelegationTargetsSlice,
  maxAgeMs: number,
): boolean {
  return (
    slice.fetchedAt !== null &&
    maxAgeMs > 0 &&
    Date.now() - slice.fetchedAt < maxAgeMs
  );
}

type SetStore = Parameters<StateCreator<AgentDelegationTargetsStore>>[0];

function patchSlice(
  set: SetStore,
  kind: AgentDelegationKind,
  patch: Partial<AgentDelegationTargetsSlice>,
) {
  set((state) => ({
    slices: { ...state.slices, [kind]: { ...state.slices[kind], ...patch } },
  }));
}

function startRequest(
  set: SetStore,
  kind: AgentDelegationKind,
  fetcher: AgentDelegationTargetsFetcher,
): Promise<AgentDelegationTarget[]> {
  patchSlice(set, kind, { isLoading: true });
  const request: Promise<AgentDelegationTarget[]> = new Promise<
    AgentDelegationTarget[]
  >((resolve) => resolve(fetcher())).then(
    (targets) => {
      if (inFlightRequests.get(kind) === request) {
        inFlightRequests.delete(kind);
        patchSlice(set, kind, {
          targets,
          fetchedAt: Date.now(),
          isLoading: false,
        });
      }
      return targets;
    },
    (error: unknown) => {
      if (inFlightRequests.get(kind) === request) {
        inFlightRequests.delete(kind);
        patchSlice(set, kind, { isLoading: false });
      }
      throw error;
    },
  );
  inFlightRequests.set(kind, request);
  return request;
}

export const createAgentDelegationTargetsSlice: StateCreator<
  AgentDelegationTargetsStore
> = (set, get) => ({
  slices: createInitialSlices(),

  ensureFresh: (kind, fetcher, maxAgeMs) => {
    const inFlight = inFlightRequests.get(kind);
    if (inFlight) {
      return inFlight;
    }
    const slice = get().slices[kind];
    if (isSliceFresh(slice, maxAgeMs)) {
      return Promise.resolve(slice.targets);
    }
    return startRequest(set, kind, fetcher);
  },

  invalidate: (kind) => {
    if (kind === undefined) {
      inFlightRequests.clear();
      set({ slices: createInitialSlices() });
      return;
    }
    inFlightRequests.delete(kind);
    patchSlice(set, kind, createEmptySlice());
  },

  resetState: () => {
    inFlightRequests.clear();
    set({ slices: createInitialSlices() });
  },
});
